import { resolveEnumValue } from './fundConstants';
import { calcPerpetualUnrealizedPnl } from '../utils/perpetualPlayConfig';
import { normalizeSymbolCode } from '@formosa/shared/utils/marketThumb';

/** 判斷是否為今日（依本地時區） */
export function isSameLocalDay(dateValue) {
  if (!dateValue) {
    return false;
  }
  const date = new Date(String(dateValue).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth()
    && date.getDate() === now.getDate()
  );
}

export function sumNumericField(orders, field) {
  if (!Array.isArray(orders)) {
    return 0;
  }
  return orders.reduce((sum, order) => {
    const value = Number(order?.[field]);
    return sum + (Number.isNaN(value) ? 0 : value);
  }, 0);
}

export function sumTodayProfit(orders, timeField = 'createTime') {
  if (!Array.isArray(orders)) {
    return 0;
  }
  return orders.reduce((sum, order) => {
    if (!isSameLocalDay(order?.[timeField])) {
      return sum;
    }
    const profitLoss = Number(order?.profitLoss);
    return sum + (Number.isNaN(profitLoss) ? 0 : profitLoss);
  }, 0);
}

/** 永續持倉未實現盈虧合計（需傳入 symbol → 現價 映射） */
export function sumPerpetualUnrealizedPnl(orders, priceBySymbol = {}) {
  if (!Array.isArray(orders)) {
    return 0;
  }
  return orders.reduce((sum, order) => {
    const symbol = normalizeSymbolCode(order?.marketSymbolCode || '');
    const thumb = priceBySymbol[symbol];
    const currentPrice = thumb?.price ?? thumb?.close ?? order?.orderPrice;
    const pnl = calcPerpetualUnrealizedPnl(
      order?.orderType,
      order?.orderPrice,
      currentPrice,
      order?.betAmount,
      order?.contractMultiple,
    );
    return sum + (Number.isNaN(pnl) ? 0 : pnl);
  }, 0);
}

export function collectPerpetualSymbolCodes(orders) {
  if (!Array.isArray(orders)) {
    return [];
  }
  const codes = orders
    .map((order) => normalizeSymbolCode(order?.marketSymbolCode || ''))
    .filter(Boolean);
  return [...new Set(codes)];
}

export function resolvePositionPairLabel(order) {
  const symbol = String(order?.marketSymbolCode || '').trim();
  if (!symbol) {
    return '';
  }
  return `${symbol}/USDT`;
}

export function resolvePositionDirectionLabel(t, tab, order) {
  if (tab === 'perpetual') {
    const type = resolveEnumValue(order?.orderType);
    if (type === 'LONG') {
      return t('funds.directionLong');
    }
    if (type === 'SHORT') {
      return t('funds.directionShort');
    }
    return type || t('common.dash');
  }

  const trend = resolveEnumValue(order?.orderTrend);
  if (trend === 'UP') {
    return t('funds.directionUp');
  }
  if (trend === 'DOWN') {
    return t('funds.directionDown');
  }
  return trend || t('common.dash');
}

export function resolvePositionAmount(order, tab) {
  if (tab === 'perpetual') {
    return order?.betAmount ?? order?.margin;
  }
  if (tab === 'futures') {
    return order?.orderAmount;
  }
  return order?.betAmount;
}

export function resolvePositionTime(order, tab) {
  if (tab === 'futures') {
    return order?.settleTime || order?.orderTime || order?.roundEndTime || '';
  }
  return order?.createTime || order?.orderTime || '';
}

export function resolvePositionStatusLabel(t, tab, order) {
  if (tab === 'timed') {
    const code = resolveEnumValue(order?.status);
    if (code === 'END') {
      return t('trade.stateSettled');
    }
    if (code === 'FAIL') {
      return order?.statusLabel || code;
    }
    return order?.statusLabel || code || t('common.dash');
  }

  if (tab === 'futures') {
    const code = resolveEnumValue(order?.state);
    if (code === 'SETTLED') {
      return t('trade.stateSettled');
    }
    if (code === 'CANCELLED') {
      return t('trade.stateCancelled');
    }
    if (code === 'BOOKED') {
      return t('trade.stateBooked');
    }
    return order?.stateLabel || code || t('common.dash');
  }

  const code = resolveEnumValue(order?.status);
  if (code === 'CLOSED') {
    return t('funds.perpetualStatusClosed');
  }
  if (code === 'LIQUIDATED') {
    return t('funds.perpetualStatusLiquidated');
  }
  if (code === 'CANCELLED') {
    return t('trade.stateCancelled');
  }
  if (code === 'OPEN') {
    return t('funds.perpetualStatusOpen');
  }
  if (code === 'PENDING') {
    return t('funds.perpetualStatusPending');
  }
  return order?.statusLabel || code || t('common.dash');
}
